import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 */
// END COPYRIGHT
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useState } from "react";
import ApiKeyErrorBanner from "./ApiKeyErrorBanner";
import ApiKeyInput from "./ApiKeyInput";
import { getApiKey, setApiKey } from "./byokKeys";
import { PROVIDERS, validateProviderKey } from "./providers";
import { API_BASE_URL } from "../apiConfig";
// #region: Styled Components
const CloseButton = styled(IconButton)({
    position: "absolute",
    right: 8,
    top: 8,
});
const CloseGlyph = styled("span")({
    fontSize: "1.25rem",
    fontWeight: 400,
    lineHeight: 1,
});
const HintIcon = styled("span")(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));
const HintText = styled("span")(({ theme }) => ({
    borderBottom: "1px dashed",
    paddingBottom: theme.spacing(0.25),
}));
const KeyRows = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
}));
const StorageHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    cursor: "help",
    fontSize: "0.7rem",
    lineHeight: 1,
}));
// Right padding clears the absolutely positioned close button.
const TitleRow = styled("span")(({ theme }) => ({
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    paddingRight: theme.spacing(6),
}));
// #endregion: Types
const ByokDialog = ({ onClose, isOpen }) => {
    const [dirtyVendors, setDirtyVendors] = useState({});
    const [keyTestResults, setKeyTestResults] = useState({});
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
    const isDirty = Object.values(dirtyVendors).some(Boolean);
    const handleDirtyChange = useCallback((vendor, dirty) => {
        setDirtyVendors((prev) => (prev[vendor] === dirty ? prev : { ...prev, [vendor]: dirty }));
    }, []);
    // Per-vendor: success/clear/new error on one row does not touch the others.
    // The banner lists every vendor that currently has a failure.
    const handleKeyResultChange = useCallback((vendor, result) => {
        setKeyTestResults((prev) => ({
            ...prev,
            [vendor]: result ? { vendor, result } : null,
        }));
    }, []);
    const keyFailures = PROVIDERS.flatMap(({ vendor }) => {
        const failure = keyTestResults[vendor];
        return failure ? [failure] : [];
    });
    // A reopened dialog starts clean, so drop the per-row state on the way out.
    const closeDialog = () => {
        setKeyTestResults({});
        setDirtyVendors({});
        onClose();
    };
    const requestClose = () => {
        if (isDirty) {
            setDiscardConfirmOpen(true);
            return;
        }
        closeDialog();
    };
    const discardAndClose = () => {
        setDiscardConfirmOpen(false);
        closeDialog();
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { fullWidth: true, maxWidth: "md", onClose: requestClose, open: isOpen, children: [_jsx(DialogTitle, { children: _jsxs(TitleRow, { children: ["API Keys", _jsx(Tooltip, { title: "API keys are stored in this browser tab for this session only and are sent to the " +
                                        `LMX server at ${API_BASE_URL}, which forwards them to neuro-san as sly_data. They ` +
                                        "are not written to disk by the app. Closing the tab clears them.", children: _jsxs(StorageHint, { variant: "caption", children: [_jsx(HintIcon, { children: "\u24D8" }), _jsx(HintText, { children: "How are my keys stored and used?" })] }) })] }) }), _jsx(CloseButton, { "aria-label": "Close API keys", onClick: requestClose, children: _jsx(CloseGlyph, { "aria-hidden": true, children: "\u00D7" }) }), _jsxs(DialogContent, { children: [_jsx(ApiKeyErrorBanner, { failures: keyFailures }), _jsx(KeyRows, { children: isOpen &&
                                    PROVIDERS.map(({ vendor, placeholder }) => (_jsx(ApiKeyInput, { initialValue: getApiKey(vendor) ?? "", onDirtyChange: handleDirtyChange, onForget: () => setApiKey(vendor, ""), onResultChange: handleKeyResultChange, onSave: (key) => setApiKey(vendor, key), onTest: (key) => validateProviderKey(vendor, key), placeholder: placeholder, vendor: vendor }, vendor))) })] })] }), _jsxs(Dialog, { open: discardConfirmOpen, onClose: () => setDiscardConfirmOpen(false), children: [_jsx(DialogTitle, { children: "Discard unsaved API keys?" }), _jsx(DialogContent, { children: _jsx(DialogContentText, { children: "You have typed a key that has not been saved. Close without saving?" }) }), _jsxs(DialogActions, { children: [_jsx(Button, { color: "inherit", onClick: () => setDiscardConfirmOpen(false), children: "Keep editing" }), _jsx(Button, { onClick: discardAndClose, children: "Close without saving" })] })] })] }));
};
export default ByokDialog;
